export const required = (message) => (value) => {
  return value ? undefined : message;
};
export const validateEmail = (email) => {
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const validatePhone = (phone) => {
  const re = /^\+1-[(]{0,1}[0-9]{3}[)]{0,1}[-\s\.]{0,1}[0-9]{3}[-\s\.]{0,1}[0-9]{4}$/;
  return re.test(String(phone).toLowerCase());
};

export const validatePassword = (pass) => {
  const re = /^^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$$/;
  return re.test(String(pass));
};

export const min =
  (message, length = 8) =>
  (value = '') =>
    value.length >= length ? undefined : message;
export const max =
  (message) =>
  (value = '', length = 50) =>
    value.length <= length ? undefined : message;
export const email = (message) => (value) => validateEmail(value) ? undefined : message;
export const phone = (message) => (value) => validatePhone(value) ? undefined : message;
export const password = (message) => (value) => validatePassword(value) ? undefined : message;

// value is a Date object
export const minDate = (message, date) => (value) => value >= date ? undefined : message;
export const maxDate = (message, date) => (value) => value <= date ? undefined : message;

export const requireAmount = (message) => (value) => {
  return !value || value === '$' ? message : undefined;
};
export const minAmount = (message, amount) => (value) => {
  const decimalValue = Number(value.replace(/[^0-9.-]+/g, ''));
  return decimalValue && decimalValue >= amount ? undefined : message;
};
export const maxAmount = (message, amount) => (value) => {
  const decimalValue = Number(value.replace(/[^0-9.-]+/g, ''));
  return decimalValue && decimalValue <= amount ? undefined : message;
};

export const composeValidators =
  (...validators) =>
  (value) =>
    validators.reduce((error, validator) => error || validator(value), undefined);

export const validateRegExpPattern = (value, pattern) => {
  const re = new RegExp(pattern);
  return re.test(value);
};

export const validateTextInputLength = (value, limits) =>
  !!(value?.toString().length >= limits.minimum && value?.toString().length <= limits.maximum);

const validateDayByMonth = (value, name, field, setDataError, options) => {
  const month = options.month || '01';
  const days31 = ['01', '03', '05', '07', '08', '10', '12'];
  const days30 = ['04', '06', '09', '11'];
  if (days31.includes(month) && (value < 1 || value > 31)) {
    setDataError((dataError) => ({
      ...dataError,
      [field.name]: `The day should be between 1 and 31.`,
    }));
    return false;
  }
  if (days30.includes(month) && (value < 1 || value > 30)) {
    setDataError((dataError) => ({
      ...dataError,
      [field.name]: `The day should be between 1 and 30.`,
    }));
    return false;
  }
  if (month === '02') {
    let daysInFebruary = 28;
    const year = options.year;
    if (year) {
      if (year % 4 === 0 || year % 100 === 0 || year % 400 === 0) {
        daysInFebruary = 29;
      }
      if (value < 1 || value > daysInFebruary) {
        setDataError((dataError) => ({
          ...dataError,
          [field.name]: `The day should be between 1 and ${daysInFebruary}.`,
        }));
        return false;
      }
    }
    if (value < 1 || value > daysInFebruary) {
      setDataError((dataError) => ({
        ...dataError,
        [field.name]: `The day should be between 1 and ${daysInFebruary}.`,
      }));
      return false;
    }
  }
  setDataError((dataError) => ({ ...dataError, [field.name]: '' }));
};

export const validateInputField = (value, name, field, setDataError, options) => {
  if (
    field.type === 'checkbox' ||
    field.required === undefined ||
    (field.required === false &&
      (value === '' ||
        typeof value === 'undefined' ||
        value === null ||
        (value && value.length === 0)))
  ) {
    return true;
  }

  if (
    name.includes('year') &&
    !['yearsOfIndustryExperience', 'yearLastTaxReturnFiled'].includes(name)
  ) {
    if (value < 1900 || value > 2100) {
      if (options && options.day && options.month && options.year) {
        validateDayByMonth(value, name, field, setDataError, options);
      }
      setDataError((dataError) => ({
        ...dataError,
        [field.name]: `Enter a valid year`,
      }));
      return false;
    }
    setDataError((dataError) => ({ ...dataError, [field.name]: '' }));
  }
  if (name.includes('day')) {
    if (options.day && options.month) {
      validateDayByMonth(value, name, field, setDataError, options);
    }
  }

  if (
    value === '' ||
    typeof value === 'undefined' ||
    value === null ||
    (value && value.length === 0) ||
    (field.type === 'selectOne' && value === 'UNINITIALIZED')
  ) {
    setDataError((dataError) => ({
      ...dataError,
      [name]: 'This is a required field',
    }));
    return false;
  }

  if (field) {
    if (field.type === 'maskInput') {
      const maskInputValid = validateRegExpPattern(value, field.pattern);
      if (!maskInputValid) {
        setDataError((dataError) => ({
          ...dataError,
          [name]: 'Format is incorrect',
        }));
        return false;
      }
      setDataError((dataError) => ({ ...dataError, [name]: '' }));
      return true;
    }
    if (field.type === 'email') {
      const emailValid = validateRegExpPattern(value, field.pattern);
      if (!emailValid) {
        setDataError((dataError) => ({
          ...dataError,
          [name]: 'Invalid email address',
        }));
        return false;
      }
      setDataError((dataError) => ({ ...dataError, [name]: '' }));
      return true;
    }
    if (field.type === 'naicsCode') {
      const maskInputValid = validateRegExpPattern(value, field.pattern);
      if (!maskInputValid) {
        setDataError((dataError) => ({
          ...dataError,
          [name]: 'Format is incorrect',
        }));
        return false;
      }
      setDataError((dataError) => ({ ...dataError, [name]: '' }));
      return true;
    }
    if (field.type === 'text' || field.type === 'textarea') {
      if (field.limits) {
        const textInputValid = validateTextInputLength(value, field.limits);
        if (!textInputValid) {
          setDataError((dataError) => ({
            ...dataError,
            [name]: `Should have between ${field.limits.minimum} and ${field.limits.maximum} characters.`,
          }));
          return false;
        }
      }
      setDataError((dataError) => ({ ...dataError, [name]: '' }));
      return true;
    }
    if (field.type === 'address') {
      const addressValid = !!value?.street_line || !!value?.deliveryLine1;
      if (!addressValid) {
        setDataError((dataError) => ({
          ...dataError,
          [name]: 'This address is invalid.',
        }));
        return false;
      }
      setDataError((dataError) => ({ ...dataError, [name]: '' }));
      return true;
    }
    if (field.type === 'date') {
      const dateValid = validateRegExpPattern(value, field.pattern);
      if (!dateValid) {
        setDataError((dataError) => ({
          ...dataError,
          [name]: 'This date is invalid or not within range.',
        }));
        return false;
      }
      setDataError((dataError) => ({ ...dataError, [name]: '' }));
      return true;
    }
    if (field.type === 'shortDate') {
      const dateValid = validateRegExpPattern(value, field.pattern);
      if (!dateValid) {
        setDataError((dataError) => ({
          ...dataError,
          [name]: 'This date is invalid or not within range.',
        }));
        return false;
      }
      setDataError((dataError) => ({ ...dataError, [name]: '' }));
      return true;
    }
    setDataError((dataError) => ({ ...dataError, [name]: '' }));
    return true;
  }
};
